import { SelectedSource, SourceType, schSourceType } from '@aloft/toolkit-shared';

import React, { useRef, useState } from 'react';
import { FieldPath, UseFormSetValue, useWatch } from 'react-hook-form';

import { selectFeatureGridActiveConflictedFields } from '../../../../redux/features/selectors/uiSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks.ts';
import { PropertyGridForm } from '../../types.ts';
import { handleUpdatePropertyData } from '../../utils.ts';
import { PropertyGridFieldProps } from '../types.ts';
import { useFieldConflictDetection } from './useFieldConflictDetection.tsx';
import { usePropertyField } from './usePropertyField.tsx';
import { useSourceOptions } from './useSourceOptions.tsx';

type UseEditableFieldParams = Pick<
  PropertyGridFieldProps,
  'columnIndex' | 'control' | 'getValues' | 'required'
> &
  Required<Pick<PropertyGridFieldProps, 'isArv'>> & {
    field: Pick<PropertyGridFieldProps['field'], 'fieldName' | 'toDisplayValue'>;
    setValue: UseFormSetValue<PropertyGridForm>;
  };

export const useEditableField = ({
  columnIndex,
  field,
  isArv,
  control,
  getValues,
  setValue,
  required = false,
}: UseEditableFieldParams) => {
  const dispatch = useAppDispatch();

  const {
    propertyId,
    isSubject,
    fieldName,
    propertyDataFieldName,
    selectedSourceFieldName,
    value,
    displayValue,
  } = usePropertyField({ columnIndex, field, isArv, control, getValues });

  const conflictedFields = useAppSelector(selectFeatureGridActiveConflictedFields);
  const columns = getValues('columns');

  const selectedSourceFieldValue = useWatch({ control, name: selectedSourceFieldName }) as
    | SelectedSource
    | undefined;
  const selectedSourceValue = selectedSourceFieldValue?.source;
  const { conflictIsResolved } = selectedSourceFieldValue ?? {};

  const [showConflictButton, setShowConflictButton] = useState(false);
  const [inputFocused, setInputFocused] = useState(false);
  const customInput = useRef<HTMLInputElement>(
    null,
  ) as React.MutableRefObject<HTMLInputElement | null>;

  const options = useSourceOptions({
    columns,
    propertyId,
    fieldName,
    toDisplayValue: field.toDisplayValue,
    getValues,
    required,
  });

  const fieldHasConflict = Object.keys(conflictedFields).includes(propertyDataFieldName);

  useFieldConflictDetection({
    propertyDataFieldName,
    fieldName,
    propertyId,
    fieldHasConflict,
    conflictIsResolved,
    options,
  });

  const updatePropertyData = () => {
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  const makeHandleOnClose = (onClose: () => void) => () => {
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    updatePropertyData();
    onClose();
  };

  const resolveSource = (
    parsedNewValue?: string,
    newSource?: SourceType,
    displayParsedValue?: string,
  ): SourceType => {
    if (newSource === schSourceType.enum.unknown && !required) return schSourceType.enum.unknown;
    const matchingOption = options.find((option) => option.value === displayParsedValue);
    if (newSource) return newSource;
    if (matchingOption) return matchingOption.source;
    if (parsedNewValue !== undefined) return 'custom';
    return 'none';
  };

  const commitSource = (resolvedSource: SourceType) => {
    setValue(`${selectedSourceFieldName}.source` as FieldPath<PropertyGridForm>, resolvedSource);
  };

  return {
    // from usePropertyField
    propertyId,
    isSubject,
    fieldName,
    propertyDataFieldName,
    selectedSourceFieldName,
    value,
    displayValue,
    // editable-field state
    conflictedFields,
    columns,
    selectedSourceFieldValue,
    selectedSourceValue,
    conflictIsResolved,
    showConflictButton,
    setShowConflictButton,
    inputFocused,
    setInputFocused,
    customInput,
    options,
    fieldHasConflict,
    updatePropertyData,
    makeHandleOnClose,
    resolveSource,
    commitSource,
  };
};
